import * as THREE from 'three';
import { bakeColored, createInstancedMesh, mergeBufferGeometries } from './batch';
import { makeSignTexture, mesh, roundedBox } from './helpers';
import type { ParkMaterials } from './materials';

function bakedMesh(proto: THREE.Object3D, material: THREE.Material, cast = true): THREE.Mesh {
  const item = new THREE.Mesh(bakeColored(proto), material);
  item.castShadow = cast;
  item.receiveShadow = true;
  return item;
}

function addStripedAwning(
  proto: THREE.Group,
  mats: ParkMaterials,
  width: number,
  y: number,
  z: number,
  depth: number,
  stripes = 6,
): void {
  const stripeWidth = width / stripes;
  for (let i = 0; i < stripes; i += 1) {
    proto.add(
      mesh(new THREE.BoxGeometry(stripeWidth + 0.005, 0.045, depth), i % 2 === 0 ? mats.red : mats.cream, {
        position: [-width / 2 + stripeWidth * (i + 0.5), y, z],
        rotation: [0.42, 0, 0],
      }),
    );
  }
  proto.add(
    mesh(new THREE.BoxGeometry(width + 0.06, 0.06, 0.06), mats.wood, {
      position: [0, y - Math.sin(0.42) * depth * 0.5, z + Math.cos(0.42) * depth * 0.5],
    }),
  );
}

/** Welcome gate: chunky pillars, a straight name board, a bulb-lit arch and two waving pennants. */
export function createEntrance(mats: ParkMaterials): THREE.Group {
  const gate = new THREE.Group();
  const proto = new THREE.Group();
  for (const x of [-1.95, 1.95]) {
    proto.add(mesh(new THREE.BoxGeometry(1.02, 0.26, 1.02), mats.stoneDark, { position: [x, 0.13, 0] }));
    proto.add(mesh(roundedBox(0.8, 2.15, 0.8, 0.1), mats.stone, { position: [x, 1.3, 0] }));
    proto.add(mesh(new THREE.BoxGeometry(0.88, 0.16, 0.88), mats.red, { position: [x, 1.05, 0] }));
    proto.add(mesh(new THREE.BoxGeometry(0.96, 0.2, 0.96), mats.red, { position: [x, 2.45, 0] }));
    proto.add(mesh(roundedBox(0.72, 0.14, 0.72, 0.05), mats.stone, { position: [x, 2.62, 0] }));
    proto.add(mesh(new THREE.SphereGeometry(0.13, 10, 8), mats.gold, { position: [x, 2.8, 0] }));
    proto.add(mesh(new THREE.CylinderGeometry(0.018, 0.022, 0.72, 6), mats.iron, { position: [x, 3.2, 0] }));
  }
  proto.add(mesh(roundedBox(3.3, 0.66, 0.3, 0.06), mats.cream, { position: [0, 2.42, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(3.34, 0.08, 0.34), mats.red, { position: [0, 2.78, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(3.34, 0.08, 0.34), mats.red, { position: [0, 2.06, 0] }));

  // Half-disc crest with a cream border and a gold star finial above the name board.
  const crestBack = new THREE.CircleGeometry(0.72, 16, 0, Math.PI);
  crestBack.translate(0, 2.8, -0.04);
  proto.add(mesh(crestBack, mats.cream));
  const crest = new THREE.CircleGeometry(0.6, 16, 0, Math.PI);
  crest.translate(0, 2.8, 0.02);
  proto.add(mesh(crest, mats.red));
  proto.add(mesh(new THREE.SphereGeometry(0.11, 10, 8), mats.gold, { position: [0, 3.56, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.18, 6), mats.gold, { position: [0, 3.44, 0] }));
  gate.add(bakedMesh(proto, mats.baked));

  const bulbs: Array<{ position: [number, number, number] }> = [];
  for (let i = 0; i <= 8; i += 1) {
    const x = -1.4 + (i / 8) * 2.8;
    if (Math.abs(x) > 0.75) bulbs.push({ position: [x, 2.86, 0.02] });
  }
  for (let i = 0; i <= 8; i += 1) {
    const angle = (i / 8) * Math.PI;
    bulbs.push({ position: [Math.cos(angle) * 0.66, 2.8 + Math.sin(angle) * 0.66, 0.06] });
  }
  gate.add(createInstancedMesh(new THREE.SphereGeometry(0.06, 8, 6), mats.lamp, bulbs, { cast: false, receive: false }));

  const sign = mesh(
    new THREE.PlaneGeometry(3.0, 0.56),
    new THREE.MeshStandardMaterial({ map: makeSignTexture('PARK', '#e24a4a', '#fff6e6', '#f2c14e'), roughness: 0.6 }),
    { cast: false, receive: false },
  );
  sign.position.set(0, 2.42, 0.16);
  gate.add(sign);

  const flagL = mesh(new THREE.BoxGeometry(0.42, 0.22, 0.03), mats.blue, { position: [-1.74, 3.42, 0], cast: false });
  const flagR = mesh(new THREE.BoxGeometry(0.42, 0.22, 0.03), mats.red, { position: [2.16, 3.42, 0], cast: false });
  gate.add(flagL, flagR);
  gate.userData.update = (elapsed: number) => {
    flagL.scale.x = 1 + Math.sin(elapsed * 3.1) * 0.1;
    flagL.rotation.y = Math.sin(elapsed * 2.2) * 0.25;
    flagR.scale.x = 1 + Math.sin(elapsed * 3.1 + 1.05) * 0.1;
    flagR.rotation.y = Math.sin(elapsed * 2.2 + 0.8) * 0.25;
  };
  return gate;
}

export function createTicketBooth(mats: ParkMaterials): THREE.Mesh {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.BoxGeometry(1.15, 0.08, 1.05), mats.stoneDark, { position: [0, 0.04, 0] }));
  proto.add(mesh(roundedBox(0.95, 1.02, 0.85, 0.08), mats.cream, { position: [0, 0.56, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.56, 0.42, 0.06), mats.charcoal, { position: [0, 0.68, 0.42] }));
  proto.add(mesh(new THREE.BoxGeometry(0.72, 0.06, 0.2), mats.wood, { position: [0, 0.46, 0.5] }));
  proto.add(mesh(new THREE.BoxGeometry(0.16, 0.06, 0.03), mats.red, { position: [0, 0.93, 0.44] }));
  proto.add(mesh(roundedBox(1.16, 0.12, 1.06, 0.03), mats.red, { position: [0, 1.12, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.78, 0.4, 4), mats.red, { position: [0, 1.38, 0], rotation: [0, Math.PI / 4, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.07, 8, 6), mats.gold, { position: [0, 1.6, 0] }));
  addStripedAwning(proto, mats, 1.1, 1.0, 0.5, 0.5, 5);
  return bakedMesh(proto, mats.baked);
}

export function createFountain(mats: ParkMaterials): THREE.Group {
  const group = new THREE.Group();
  const stone = new THREE.Group();
  stone.add(mesh(new THREE.CylinderGeometry(1.02, 1.08, 0.3, 24), mats.stone, { position: [0, 0.15, 0] }));
  const rim = new THREE.TorusGeometry(0.98, 0.075, 8, 28);
  rim.rotateX(Math.PI / 2);
  rim.translate(0, 0.31, 0);
  stone.add(mesh(rim, mats.stoneDark));
  stone.add(mesh(new THREE.CylinderGeometry(0.15, 0.22, 0.72, 10), mats.stone, { position: [0, 0.6, 0] }));
  stone.add(mesh(new THREE.CylinderGeometry(0.46, 0.3, 0.14, 16), mats.stone, { position: [0, 1.0, 0] }));
  stone.add(mesh(new THREE.ConeGeometry(0.09, 0.32, 8), mats.gold, { position: [0, 1.22, 0] }));
  group.add(bakedMesh(stone, mats.baked));

  const lower = new THREE.CylinderGeometry(0.92, 0.92, 0.06, 24);
  lower.translate(0, 0.3, 0);
  const upper = new THREE.CylinderGeometry(0.4, 0.4, 0.05, 16);
  upper.translate(0, 1.08, 0);
  const drop = new THREE.SphereGeometry(0.09, 8, 6);
  drop.translate(0, 1.44, 0);
  const water = new THREE.Mesh(mergeBufferGeometries([lower, upper, drop]), mats.water);
  water.castShadow = false;
  water.receiveShadow = true;
  group.add(water);
  group.userData.update = (elapsed: number) => {
    mats.water.emissiveIntensity = 0.18 + Math.sin(elapsed * 2.4) * 0.06;
  };
  return group;
}

/** Snack kiosk: butter-yellow hut, striped awning, red roof and an ice-cream finial. */
export function createKiosk(mats: ParkMaterials): THREE.Mesh {
  const proto = new THREE.Group();
  proto.add(mesh(roundedBox(1.55, 0.1, 1.25, 0.05), mats.curb, { position: [0, 0.05, 0] }));
  proto.add(mesh(roundedBox(1.25, 0.96, 0.95, 0.08), mats.butter, { position: [0, 0.56, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.82, 0.4, 0.06), mats.charcoal, { position: [0, 0.7, 0.47] }));
  proto.add(mesh(new THREE.BoxGeometry(1.02, 0.07, 0.26), mats.wood, { position: [0, 0.5, 0.56] }));
  proto.add(mesh(new THREE.BoxGeometry(0.05, 0.5, 0.42), mats.charcoal, { position: [-0.65, 0.72, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.04, 0.3, 0.26), mats.cream, { position: [-0.68, 0.74, 0] }));
  addStripedAwning(proto, mats, 1.3, 1.08, 0.58, 0.56, 6);
  proto.add(mesh(roundedBox(1.42, 0.14, 1.12, 0.04), mats.red, { position: [0, 1.1, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.92, 0.44, 4), mats.red, { position: [0, 1.38, 0], rotation: [0, Math.PI / 4, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.26, 0.58, 8), mats.woodLight, { position: [0, 1.86, 0], rotation: [Math.PI, 0, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.3, 9, 7), mats.pink, { position: [0, 2.22, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.26, 9, 7), mats.cream, { position: [0.04, 2.52, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.1, 7, 6), mats.red, { position: [0.08, 2.78, 0] }));
  return bakedMesh(proto, mats.baked);
}

/**
 * Drink kiosk: sky hut, blue-cream awning, teal roof and a warm orange soda-cup finial.
 * The topper is deliberately the complementary warm hue to the teal roof so it pops at park zoom:
 * orange cup (wider at the rim), cream label band, cream lid, red straw and a lemon-slice garnish.
 */
export function createDrinkKiosk(mats: ParkMaterials): THREE.Mesh {
  const proto = new THREE.Group();
  proto.add(mesh(roundedBox(1.45, 0.1, 1.2, 0.05), mats.curb, { position: [0, 0.05, 0] }));
  proto.add(mesh(roundedBox(1.18, 0.98, 0.9, 0.08), mats.sky, { position: [0, 0.56, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.78, 0.4, 0.06), mats.charcoal, { position: [0, 0.7, 0.44] }));
  proto.add(mesh(new THREE.BoxGeometry(0.96, 0.07, 0.24), mats.wood, { position: [0, 0.5, 0.52] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.13, 0.11, 0.34, 8), mats.orange, { position: [0.42, 0.76, 0.38] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.05, 8), mats.cream, { position: [0.42, 0.95, 0.38] }));
  addStripedAwning(proto, mats, 1.22, 1.08, 0.54, 0.52, 5);
  proto.add(mesh(roundedBox(1.34, 0.12, 1.06, 0.04), mats.blue, { position: [0, 1.1, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.86, 0.38, 4), mats.teal, { position: [0, 1.34, 0], rotation: [0, Math.PI / 4, 0] }));
  // Topper: same footprint/size class as WU-012, recoloured and re-tapered for contrast.
  proto.add(mesh(new THREE.CylinderGeometry(0.34, 0.27, 0.54, 12), mats.orange, { position: [0, 1.86, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.335, 0.32, 0.13, 12), mats.cream, { position: [0, 1.88, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.37, 0.37, 0.08, 12), mats.cream, { position: [0, 2.17, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.3, 0.34, 0.05, 12), mats.red, { position: [0, 2.23, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.07, 0.5, 0.07), mats.red, { position: [0.1, 2.46, 0], rotation: [0, 0, -0.2] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.04, 12), mats.yellow, { position: [-0.26, 2.32, 0.04], rotation: [Math.PI / 2, 0, 0.25] }));
  return bakedMesh(proto, mats.baked);
}
